import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';

/**
 * The contact gate — the cross-channel frequency ceiling.
 *
 * Backend: core-service `/api/v1/contact-gate`. The ceiling is what stops five
 * campaigns each honouring their own `suppress_recent_days` from stacking five
 * messages onto one person in a week; it counts every channel, because the
 * question is always "have we contacted them at all".
 *
 * It ships OFF for every organization and blocks nobody until somebody turns it
 * on. `preview` exists so the number is chosen from real history rather than
 * taste — picking one and applying it to live campaigns produces silent
 * under-sending, which nobody notices for days.
 */
export interface ContactTouchPolicy {
  /** False when there is no ceiling — the default for every organization. */
  enabled: boolean;
  /** Null until a policy has ever been saved. */
  maxTouches: number | null;
  windowDays: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Whether a row exists at all, as distinct from one that exists and is off. */
  configured: boolean;
}

export interface GatePreviewRow {
  /** The normalised join key — an email, or a phone reduced to its last ten digits. */
  matchKey: string;
  touches: number;
}

export interface GatePreview {
  maxTouches: number;
  windowDays: number;
  /** Distinct people who would be blocked right now under these numbers. */
  peopleOverTheLimit: number;
  /** Everything actually delivered in the window, so the number above has a denominator. */
  totalSentInWindow: number;
  /** How many rows `busiest` is capped at — named so it is not read as the whole answer. */
  sampleSize: number;
  busiest: GatePreviewRow[];
}

export const contactGateService = {
  getPolicy: async (): Promise<ApiResponse<ContactTouchPolicy>> => {
    try { return (await axiosInstance.get('/v1/contact-gate/policy')).data; }
    catch (e: any) { return apiError(e, 'Failed to load the contact gate policy'); }
  },

  setPolicy: async (body: {
    enabled: boolean; maxTouches: number; windowDays: number;
  }): Promise<ApiResponse<ContactTouchPolicy>> => {
    try { return (await axiosInstance.put('/v1/contact-gate/policy', body)).data; }
    catch (e: any) { return apiError(e, 'Failed to save the contact gate policy'); }
  },

  preview: async (maxTouches: number, windowDays: number): Promise<ApiResponse<GatePreview>> => {
    try {
      return (await axiosInstance.get('/v1/contact-gate/preview', {
        params: { maxTouches, windowDays },
      })).data;
    } catch (e: any) { return apiError(e, 'Failed to preview the contact gate'); }
  },
};
